// SharedCollectionTab.jsx - Wrapper for shared collection content
import React from 'react';
import CollectionHeader from './CollectionHeader';
import CollectionControls from './CollectionControls';
import CollectionGrid from './CollectionGrid';
import CollectionEmptyState from './CollectionEmptyState';

/**
 * Wrapper component for displaying a shared collection
 * with the same layout as user's own collection
 */
function SharedCollectionTab({
  // Collection data
  sharedCollection,
  games = [],
  loading = false,
  filteredCount = 0,

  // State and handlers (from parent hooks)
  showEditControls,
  onToggleEditControls,
  collectionSearchQuery,
  onSearchQueryChange,
  featureDropdownRef,
  featureSearchQuery,
  onFeatureSearchChange,
  showFeatureDropdown,
  featureSearchResults,
  onAddFeature,
  selectedFeatures,
  onRemoveFeature,
  sortBy,
  onSortByChange,
  sortOrder,
  onToggleSortOrder,
  showToBePlayedOnly,
  onToggleToBePlayedOnly,
  filterMode,
  onFilterModeChange,

  // Game interaction handlers
  onGameClick,
  onAddToCollection,
  onPreviewGame,

  // Grid props
  hasMore,
  onLoadMore,
  itemsPerLoad
}) {
  const collectionName = sharedCollection?.name || 'Shared Library';
  const ownerUsername = sharedCollection?.owner_username;
  const displayName = ownerUsername ? `${collectionName} (from ${ownerUsername})` : collectionName;

  return (
    <div className="shared-collection-tab">
      {/* Collection Header - Read-only for shared collections */}
      <CollectionHeader
        collectionName={displayName}
        setCollectionName={() => {}} // Read-only
        filteredCollectionCount={filteredCount}
        isEditingCollectionName={false}
        setIsEditingCollectionName={() => {}} // Read-only
        onSaveCollectionName={() => {}} // Read-only
        user={null} // Shared collections don't have editing user
        onShareModalOpen={() => {}} // Read-only
        onToggleEditControls={onToggleEditControls}
        showEditControls={showEditControls}
        onAddGame={() => {}} // Disabled for shared collections
        filterMode={filterMode}
        onFilterModeChange={onFilterModeChange}
        pendingChangesCount={0} // No pending changes for shared collections
        onRefreshCollection={() => {}} // Read-only
        isSharedCollection={true}
      />

      {/* Collection Controls - Full functionality for filtering and search */}
      <CollectionControls
        showEditControls={showEditControls}
        collectionSearchQuery={collectionSearchQuery}
        onSearchQueryChange={onSearchQueryChange}
        featureDropdownRef={featureDropdownRef}
        featureSearchQuery={featureSearchQuery}
        onFeatureSearchChange={onFeatureSearchChange}
        showFeatureDropdown={showFeatureDropdown}
        featureSearchResults={featureSearchResults}
        onAddFeature={onAddFeature}
        selectedFeatures={selectedFeatures}
        onRemoveFeature={onRemoveFeature}
        sortBy={sortBy}
        onSortByChange={onSortByChange}
        sortOrder={sortOrder}
        onToggleSortOrder={onToggleSortOrder}
        showToBePlayedOnly={showToBePlayedOnly}
        onToggleToBePlayedOnly={onToggleToBePlayedOnly}
      />

      {/* Collection Grid or Empty State */}
      {games.length === 0 && !loading ? (
        <CollectionEmptyState
          title="No games found in this shared collection"
          subtitle="The owner hasn't added any games yet, or try adjusting your filters."
          icon="🎮"
        />
      ) : (
        <CollectionGrid
          games={games}
          loading={loading}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          onGameClick={onGameClick}
          onAddToCollection={onAddToCollection}
          onPreviewGame={onPreviewGame}
          emptyMessage="No games found in this shared collection"
          emptySubtext="Try adjusting your filters."
          itemsPerLoad={itemsPerLoad}
        />
      )}
    </div>
  );
}

export default SharedCollectionTab;
